import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { configureApiClient } from '../api/client'
import * as session from './session'
import { getAccessToken, getRefreshToken } from './tokenStore'
import type { CurrentUser } from './types'

/**
 * Wire the API client to the token store once, at module load — before any
 * component can issue a request. Doing this in an effect would leave the very
 * first render's queries unauthenticated.
 */
configureApiClient({
  getAccessToken,
  onUnauthorized: session.refreshSession,
})

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous'

export interface AuthContextValue {
  status: AuthStatus
  user: CurrentUser | null
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  /** Re-reads the profile, e.g. after confirming an email address. */
  reload: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading')
  const [user, setUser] = useState<CurrentUser | null>(null)

  const load = useCallback(async () => {
    // `GET /me` with an expired-or-absent access token returns 401, which the API
    // client turns into a refresh-and-retry. So this one call is also the whole
    // "restore my session on reload" path: on a fresh tab the access token is
    // gone (memory only) but the refresh token is not.
    try {
      setUser(await session.fetchMe())
      setStatus('authenticated')
    } catch {
      setUser(null)
      setStatus('anonymous')
    }
  }, [])

  useEffect(() => {
    // No refresh token means there is nothing to restore — skip the round trip
    // rather than making every first-time visitor wait for a 401.
    if (getRefreshToken() === null) {
      setStatus('anonymous')
      return
    }

    void load()
  }, [load])

  const signIn = useCallback(
    async (email: string, password: string) => {
      await session.login(email, password)
      await load()
    },
    [load],
  )

  const signOut = useCallback(async () => {
    await session.logout()
    setUser(null)
    setStatus('anonymous')
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, signIn, signOut, reload: load }),
    [status, user, signIn, signOut, load],
  )

  return <AuthContext value={value}>{children}</AuthContext>
}

export function useAuth(): AuthContextValue {
  const context = use(AuthContext)
  if (context === null) {
    throw new Error('useAuth must be used inside <AuthProvider>.')
  }
  return context
}
